import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { useAuth } from '@/contexts/AuthContext';
import { LoginFormData } from '@/types';

const loginSchema = z.object({
  email: z.string().email('Email inválido').min(1, 'Email é obrigatório'),
  senha: z.string().min(1, 'Senha é obrigatória'),
  codigoTenant: z.string()
    .min(1, 'Código do tenant é obrigatório')
    .length(3, 'Código do tenant deve ter 3 caracteres')
    .regex(/^(SYS|[0-9]{3})$/i, 'Código deve ser SYS ou conter apenas números'),
});

export const LoginPage: React.FC = () => {
  const { login, isLoading } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginNotice, setLoginNotice] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      setIsSubmitting(true);
      setLoginNotice(null);
      await login(data);
      // O redirecionamento é feito automaticamente pelo AppContent
      // baseado no estado de autenticação
    } catch (error) {
      // Mostra mensagem específica na página
      const err: any = error;
      const status = err?.response?.status;
      const retryAfter = err?.response?.headers?.['retry-after'] || err?.response?.headers?.['Retry-After'];
      const retryAfterSec = retryAfter ? Number(retryAfter) : undefined;

      if (status === 429) {
        const backendMessage = err?.response?.data?.error || err?.response?.data?.message;
        if (backendMessage) {
          setLoginNotice(backendMessage);
        } else {
          const msg = retryAfterSec
            ? `Muitas tentativas de login. Aguarde ${Math.ceil(retryAfterSec / 60)} minutos (${retryAfterSec}s) e tente novamente.`
            : 'Muitas tentativas de login. Aguarde alguns minutos e tente novamente.';
          setLoginNotice(msg);
        }
      } else {
        const backendMessage = err?.response?.data?.error || err?.response?.data?.message;
        if (backendMessage) {
          setLoginNotice(backendMessage);
        } else {
          const key = `loginAttempts:${data.email.toLowerCase()}`;
          const attempts = Number(localStorage.getItem(key) || '0');

          if (attempts === 20) {
            setLoginNotice('Você realizou 20 tentativas sem sucesso. Por segurança, tente novamente mais tarde.');
          } else {
            setLoginNotice('Credenciais inválidas. Verifique email, senha e o código do tenant.');
          }
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          
          <img
            src="/imagem/logo-nome.png"
            alt="Systagio Logo"
            className="mx-auto h-32 w-auto object-contain sm:h-40"
          />
          <p className="mt-2 text-sm text-gray-600">
            CODITECH - Faça login em sua conta
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Entrar</CardTitle>
            <CardDescription>
              Digite suas credenciais para acessar o sistema
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  {...register('email')}
                  className={errors.email ? 'border-error-500' : ''}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-error-600">{errors.email.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="senha">Senha</Label>
                <Input
                  id="senha"
                  type="password"
                  placeholder="Sua senha"
                  {...register('senha')}
                  className={errors.senha ? 'border-error-500' : ''}
                />
                {errors.senha && (
                  <p className="mt-1 text-sm text-error-600">{errors.senha.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="codigoTenant">Código</Label>
                <Input
                  id="codigoTenant"
                  type="text"
                  placeholder="Digite o código"
                  maxLength={3}
                  {...register('codigoTenant')}
                  className={errors.codigoTenant ? 'border-error-500' : ''}
                />
                {errors.codigoTenant && (
                  <p className="mt-1 text-sm text-error-600">{errors.codigoTenant.message}</p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={isSubmitting || isLoading}
              >
                {isSubmitting || isLoading ? 'Entrando...' : 'Entrar'}
              </Button>
            </form>

            {loginNotice && (
              <div className="mt-4 rounded border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-800" role="alert">
                {loginNotice}
              </div>
            )}

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                Novo por aqui?{' '}
                <Link
                  to="/register"
                  className="font-medium text-primary-600 hover:text-primary-500"
                >
                  Cadastre-se e comece grátis
                </Link>
              </p>
            </div>

            {/* <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                Não tem uma conta?{' '}
                <Link
                  to="/register"
                  className="font-medium text-primary-600 hover:text-primary-500"
                >
                  Cadastre-se aqui
                </Link>
              </p>
            </div> */}

            {/* <div className="mt-4 text-center">
            <Link
              to="/esqueci-senha"
              className="text-sm text-primary-600 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
              tabIndex={0}
              aria-label="Esqueci minha senha"
            >
              Esqueci minha senha
            </Link>
          </div> */}


          </CardContent>
        </Card>
      </div>
    </div>
  );
};
